import React, { useState } from 'react';
import BentoCard from '../components/BentoCard';
import CognitiveConsultantsLogo from '../images/partnerLogos/cognitive_consultants_llc_full.png';
import BigBrotherLogo from '../images/partnerLogos/bbbs.png';
import BrighterHealthLogo from '../images/partnerLogos/brighterhealth.png';
import CedarCreekLogo from '../images/partnerLogos/cedar-creek-logo.png';
import ElesLogo from '../images/partnerLogos/eles.jpeg';
import CommunityMentalHealth from '../images/partnerLogos/cmh.jpeg';
import CasaLogo from '../images/partnerLogos/casaLogo.png';
import ChildAndFamily from '../images/partnerLogos/childAndFamily.jpg';


const Resources = () => {
  const [filter, setFilter] = useState('All');

  const categories = ['All', 'Crisis Support', 'Community Partners', 'For Schools'];

  // Data from PRD 
  const resources = [
    {
      title: "Cognitive Consultants",
      category: "Community Partners",
      desc: "Mental health and substance abuse treatment in Mid Michigan. Evidence-based services for trauma, depression, and anxiety.",
      link: "https://www.cognitiveconsultantsllc.com/",
      color: "bg-blue-50",
      logo: CognitiveConsultantsLogo
    },
    {
      title: "Cedar Creek Hospital",
      category: "Crisis Support",
      desc: "Inpatient treatment for adolescents and adults struggling with severe depression, anxiety, or behavioral health issues.",
      link: "https://cedarcreekhospital.com/",
      color: "bg-red-50",
      logo: CedarCreekLogo
    },
    {
      title: "Ele’s Place",
      category: "Community Partners",
      desc: "A healing center for grieving children and teens. Provides peer support groups throughout the school year.",
      link: "https://elesplace.org/branch-locations/capital-region",
      color: "bg-yellow-50",
      logo: ElesLogo
    },
    {
      title: "Sister Swae",
      category: "Community Partners",
      desc: "Advocacy and empowerment for young women of color through mentorship, storytelling, and community resources.",
      link: "https://www.sistersswae.org/",
      color: "bg-pink-50"
    },
    {
      title: "Child and Family Charities",
      category: "Community Partners",
      desc: "Serving 20,000+ individuals annually with services spanning prevention, intervention, treatment, and recovery.",
      link: "https://childandfamily.org/",
      color: "bg-green-50",
      logo: ChildAndFamily
    },
    {
      title: "CASA for Children",
      category: "Crisis Support",
      desc: "Providing volunteer advocates for abused and neglected children within the Barry, Eaton, and Ingham County courts.",
      link: "https://www.casaforkidsinc.org/",
      color: "bg-orange-50",
      logo: CasaLogo
    },
    {
      title: "Brighter Health",
      category: "Community Partners",
      desc: "Compassionate counseling support tailored to your unique journey.",
      link: "https://www.mibrighterhealth.com/",
      color: "bg-teal-50",
      logo: BrighterHealthLogo
    },
    {
      title: "Big Brothers Big Sisters",
      category: "Community Partners",
      desc: "Creating one-to-one mentoring relationships that ignite the power and promise of youth.",
      link: "https://bbbsmcr.org/",
      color: "bg-purple-50",
      logo: BigBrotherLogo
    },
    {
      title: "Community Mental Health (CMHA-CEI)",
      category: "Crisis Support",
      desc: "Public agency serving Clinton, Eaton, and Ingham counties. Crisis support for serious behavioral health needs.",
      link: "https://www.ceicmh.org/",
      color: "bg-blue-100",
      logo: CommunityMentalHealth
    }
  ];

  const filteredResources = filter === 'All' 
    ? resources 
    : resources.filter(r => r.category === filter);

  return (
    <main className="max-w-7xl mx-auto p-4 lg:p-8 space-y-8">
      
      {/* 1. EMERGENCY BANNER  */}
      <div className="bg-red-600 text-white p-4 rounded-xl flex flex-col md:flex-row items-center justify-between shadow-lg animate-pulse">
        <div className="flex items-center gap-3">
            <span className="text-3xl">🆘</span>
            <div>
                <h3 className="font-bold text-lg">In Crisis? Help is available 24/7.</h3>
                <p className="text-sm text-red-100">If you or someone you know is in danger, please call 988 immediately.</p>
            </div>
        </div>
        <a href="tel:988" className="mt-4 md:mt-0 bg-white text-red-600 px-6 py-2 rounded-full font-bold hover:bg-red-50 transition-colors">
            Call 988
        </a>
      </div>

      {/* HEADER */}
      <div className="text-center py-8">
        <h1 className="text-5xl font-heading font-bold text-primary-navy mb-4">Digital Toolkit</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A curated network of local partners and trusted resources for families in Mid-Michigan.
        </p>
      </div>

      {/* 2. FILTER BUTTONS  */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {categories.map((cat) => (
            <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-full font-bold transition-all border-2 border-transparent ${
                    filter === cat 
                    ? 'bg-primary-navy text-white shadow-lg scale-105 border-primary-navy' 
                    : 'bg-white text-gray-500 hover:bg-gray-100 hover:border-gray-200'
                }`}
            >
                {cat}
            </button>
        ))}
      </div>

      {/* 3. RESOURCE GRID (Bento Layout) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map((res, index) => (
            <BentoCard 
                key={index} 
                title={res.title} 
                color={res.color}
                className="hover:-translate-y-1 transition-transform duration-200 flex flex-col h-full min-h-[300px]" // Added min-h
            >
                {/* Flex container to push link to bottom */}
                <div className="flex flex-col justify-between h-full">
                    <div>
                        <span className="inline-block px-2 py-1 bg-white/60 rounded text-xs font-bold text-gray-600 mb-3 border border-gray-100">
                            {res.category}
                        </span>
                        <p className="text-gray-700 leading-relaxed font-medium">{res.desc}</p>
                    </div>
                    
                    {/* Link Section - Pushed to bottom with margin-top */}
                    <div className="mt-auto pt-6">
                        {res.link !== "#" ? (
                            <a 
                                href={res.link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center text-primary-navy font-black hover:gap-2 transition-all group"
                            >
                                Visit Website <span className="group-hover:translate-x-1 transition-transform ml-1">→</span>
                            </a>
                            
                        ) : (
                             <span className="text-gray-400 text-sm font-bold cursor-not-allowed">Info only</span>
                        )}
                         {res.logo && (
                            <img 
                                src={res.logo} 
                                alt={`${res.title} logo`} 
                                className="absolute bottom-6 right-6 h-10 max-w-[100px] w-auto object-contain" 
                            />
                        )}
                    </div>
                </div>
            </BentoCard>
        ))}
        <BentoCard className="backdrop-blur-md text-primary-navy flex flex-col justify-center items-center text-center border-[3px] border-primary-navy relative overflow-hidden min-h-[300px]">
            {/* Subtle Texture */}
            <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col items-center">
                <span className="text-5xl mb-4">🤝</span>
                <h3 className="text-2xl font-heading font-bold mb-2">Become a Partner</h3>
                <p className="text-primary-navy/80 mb-6 text-sm font-medium max-w-xs">
                    Do you offer youth mental health services in Michigan? Join our network.
                </p>
                <a 
                  href="mailto:beapositivesomebody@gmail.com?subject=Partner%20Network%20Application&body=Hello%2C%0D%0A%0D%0AWe%20offer%20mental%20health%20services%20in%20Michigan%20and%20would%20like%20to%20join%20your%20resource%20network.%0D%0A%0D%0AOrganization%20Name%3A%20%5BName%5D%0D%0AServices%20Provided%3A%20%5BServices%5D%0D%0AWebsite%3A%20%5BWebsite%5D%0D%0A%0D%0AThank%20you!" 
                  className="bg-accent-yellow text-primary-navy px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition-colors shadow-[4px_4px_0px_#020617] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none border-2 border-primary-navy"
                >
                    Join Network
                </a>
            </div>
        </BentoCard>
      </div>

    </main>
  );
};

export default Resources;